// Copyright 2025 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { MeasurementWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementModuleCatalog";
import { FileDropbox, FileToUpload, FileUploadStatus } from "controls/file-dropbox/FileDropbox";
import { FileTree } from "controls/file-tree/FileTree";
import { NodeModel } from "controls/file-tree/TreeNode";
import { Toast } from "controls/toast/Toast";
import { ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { LicenseKind, SorXMeasurementTypeEnum } from "datatypes/SoramaWebApi";
import { SorXMetadataFull } from "datatypes/SorXFormat";
import { sorXFilePathToId, SorXMeasurement, sorXMeasurementToReportableMeasurement } from "datatypes/SorXMeasurement";
import { NotSupportedError } from "errors/NotSupportedError";
import { FileNameExtensions } from "extensions/FileNameExtensions";
import { parseSorXFile } from "helpers/SorxHelpers";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { GoogleAnalytics } from "libraries/GoogleAnalytics/GoogleAnalytics";
import { ProgressIndicator } from "libraries/ProgressIndicator/ProgressIndicator";
import { IWorkflowModuleInstance } from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { SpecialMeasurementReportGenerator } from "modules/workflows/Measurement/SpecialMeasurements/SpecialMeasurementReportGenerator/SpecialMeasurementReportGenerator";
import { CamIV64File } from "services/Hardware/CamHTTP/CamIV64File";
import { CamIV64Helpers } from "services/Hardware/CamHTTP/CamIV64Helpers";
import { CamIV64Service } from "services/Hardware/CamHTTP/CamIV64Service";
import { ICamApi } from "services/Hardware/CamHTTP/ICamApi";
import { ILicenseBundleService } from "services/LicenseBundle/ILicenseBundleService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { PrpdDataService } from "services/ReportEx/PrpdDataService";
import { ReportExService } from "services/ReportEx/ReportExService";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";
import { v4 as uuidv4 } from "uuid";
import { EditMeasurementModalState } from "../EditMeasurement/EditMeasurementModal";
import { PreviewMeasurementModalState } from "../PreviewMeasurement/PreviewMeasurementModal";

class DownloadEvent {
    file: NodeModel;
    downloadProgressCallback: (progress: ProgressIndicator) => void;
    directly?: boolean;
}

@autoinject()
export class ManageReportModule {
    private subscriptions: Disposable[] = [];
    private moduleInstance: IWorkflowModuleInstance;
    private fileTree: FileTree;
    private fileDropbox: FileDropbox;
    private fileList: CamIV64File[] = [];
    private licenseKinds: LicenseKind[] = [];
    private isImportSectionExpanded = false;

    private favoriteMeasurements: CamIV64File[] = [];

    public toastId: string = uuidv4();

    private readonly REPORTING_FEATURE_TAG = "feature_reports";
    private readonly LEAK_RATE_CONVERSION_TAG = "feature_leak_rate_conversion";
    private uploadedSorXReportables: ReportableMeasurement<SorXMeasurement>[] = [];
    private uploadedSorXSelection: { [id: string]: boolean } = {};
    private readonly sorxExtension = "sorx";

    private readonly favoritesPrefix = "Favorites/";
    private readonly deviceFilesPrefix = "Device Files/";
    private readonly importedFilesPrefix = "Imported Files/";

    protected isImportExpanded = false;
    protected isLoadingFilesFromDevice = false;

    private reportableFilePromise: {
        [filePath: string]: Promise<ReportableMeasurement<SorXMeasurement> | undefined> | null;
    } = {};

    // Make sure the report generation is disabled when we are still loading checked files
    private fileLoadingCounter = 0;
    protected get busyLoadingFiles() {
        return this.fileLoadingCounter > 0;
    }

    constructor(
        private readonly camApi: ICamApi,
        private readonly bindingEngine: BindingEngine,
        private readonly camIV64Service: CamIV64Service,
        private readonly reportService: ReportExService,
        private readonly shellViewManager: ShellViewManager,
        private readonly workflowModeService: WorkflowModeService,
        private readonly reportGenerator: SpecialMeasurementReportGenerator,
        private readonly projectManagementService: IProjectManagementService,
        private readonly licenseBundleService: ILicenseBundleService,
        private readonly prpdDataService: PrpdDataService
    ) {}

    async bind() {
        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.camIV64Service, "selectedDeviceId")
                .subscribe(() => this.selectedDeviceIdChanged()),

            this.bindingEngine.propertyObserver(this, "fileList").subscribe(() => this.fileTree?.refresh()),

            this.bindingEngine.propertyObserver(this, "favoriteMeasurements").subscribe(() => {
                this.fileTree?.refresh();
            }),
        ];

        this.isImportSectionExpanded = !this.isConnected;

        if (this.isConnected) {
            await this.selectedDeviceIdChanged();
        }

        this.reportService.removeAllReports();
    }

    public unbind(): void {
        this.subscriptions.forEach((sub) => sub.dispose());
        this.subscriptions = [];
    }

    @computedFrom("licenseKinds")
    protected get hasReportLicense(): boolean {
        return this.licenseKinds.some((l) => l.LicenseKindTag == this.REPORTING_FEATURE_TAG);
    }
    @computedFrom("licenseKinds")
    protected get hasLeakRateConversionFeatureLicense(): boolean {
        return this.licenseKinds.some((l) => l.LicenseKindTag == this.LEAK_RATE_CONVERSION_TAG);
    }

    // Only show unlock alert when CAM is connected but no reporting license
    protected get showUnlockAdditionalFeatures(): boolean {
        return this.isConnected && !this.isLoadingFilesFromDevice && !this.hasReportLicense;
    }

    protected get canGenerateReportFromDevice(): boolean {
        return this.isConnected && this.hasReportLicense;
    }

    // Only show import section as collapsible when we also are showing the file tree
    protected get showCollapsibleImportSection(): boolean {
        return this.showFileTree;
    }

    // Only show file tree when connected and have reporting license, or when there are uploaded files
    protected get showFileTree(): boolean {
        return this.canGenerateReportFromDevice || this.hasUploadedFiles;
    }

    // Dropbox should always be available, but just hidden when collapsible and not expanded
    protected get showFileDropbox(): boolean {
        if (this.showCollapsibleImportSection && !this.isImportSectionExpanded) return false;
        return true;
    }

    protected get hasFavoriteMeasurements(): boolean {
        return this.favoriteMeasurements.length > 0;
    }

    protected get hasDeviceFiles(): boolean {
        return this.fileList.length > 0;
    }

    protected get hasUploadedFiles(): boolean {
        return this.uploadedSorXReportables.length > 0;
    }

    protected get isConnected(): boolean {
        return !!this.camIV64Service.selectedDeviceId;
    }

    protected get selectedDeviceName(): string {
        return CamIV64Helpers.getDeviceName(this.camIV64Service.selectedDeviceId);
    }

    protected get hasMixedMeasurementTypes(): boolean {
        return this.reportService.hasMixedMeasurementTypesSelected;
    }

    protected get editFileFunction(): ((file: NodeModel) => Promise<void>) | null {
        return this.reportService.canEditOrGenerateReport ? this.editFile.bind(this) : null;
    }

    protected get generateReportFunction(): (() => Promise<void>) | null {
        return this.reportService.canEditOrGenerateReport ? this.generateReport.bind(this) : null;
    }

    private async selectedDeviceIdChanged(): Promise<void> {
        this.isLoadingFilesFromDevice = true;

        const serialNumber = CamIV64Helpers.getSerialNumber(this.camIV64Service.selectedDeviceId);

        // First check if user can actually do device reporting (there is a device feature license for it)
        try {
            this.licenseKinds = await this.licenseBundleService.getActiveBundlesLicenseKinds(serialNumber);
        } catch (e) {
            console.error("Error loading license information", e);
            Toast.error(
                "Failed to load license information to determine if reporting features are available. Please try again.",
                "Load Error",
                this.toastId
            );
            this.licenseKinds = [];
            this.fileList = [];
            this.favoriteMeasurements = [];
        }

        //Early exit if no reporting license
        if (!this.hasReportLicense) {
            this.isLoadingFilesFromDevice = false;
            return;
        }

        // Clear any previously selected reports since we're loading files from a different device
        this.reportService.removeAllReports();

        // Get the files from the device
        try {
            this.fileList = await this.getAllFilesFromCAMiV64();
            this.favoriteMeasurements = await this.getFavoriteMeasurements();
        } catch (e) {
            console.error("Error loading device files", e);
            Toast.error(
                "Failed to load device files. Make sure your device is connected and up-to-date.",
                "Load Error",
                this.toastId
            );
            this.licenseKinds = [];
            this.fileList = [];
            this.favoriteMeasurements = [];
        }

        this.isLoadingFilesFromDevice = false;
    }

    public openHardwareSelection() {
        this.shellViewManager.openRightSideBar(MeasurementWorkflowModuleCatalog.hardwareSelectionModule);
    }

    public async getAllFilesFromCAMiV64(): Promise<CamIV64File[]> {
        this.isLoadingFilesFromDevice = true;
        const files = await this.camApi.getCAMiV64Files();
        this.isLoadingFilesFromDevice = false;
        return files.map((file) => new CamIV64File(file.filePath, file.createDate));
    }

    public async getFavoriteMeasurements(): Promise<CamIV64File[]> {
        this.isLoadingFilesFromDevice = true;
        const files = await this.camApi.getFavoriteMeasurements();
        this.isLoadingFilesFromDevice = false;
        return files.map((file) => new CamIV64File(file.filePath, file.createDate));
    }

    // This is how the fileTree gets its files
    public refreshAndGetUnifiedTreeRoot(): NodeModel | void {
        // Build a set of favorite file names for quick lookup
        const favoriteFileNames = new Set(
            this.favoriteMeasurements.map((f) => f.filePath.replace(/^.*[\\/]/, "").toLowerCase())
        );

        // Favorites go into a "Favorites/" top-level folder
        const favoritePaths = this.favoriteMeasurements.map((file) => `${this.favoritesPrefix + file.filePath}`);

        // Device files exclude favorites (they are shown exclusively in the Favorites section)
        const deviceFilePaths = this.fileList
            .filter((file) => !favoriteFileNames.has(file.filePath.replace(/^.*[\\/]/, "").toLowerCase()))
            .map((file) => `${this.deviceFilesPrefix + file.filePath}`);

        const importedFilePaths = this.uploadedSorXReportables.map((f) => `${this.importedFilesPrefix + f.id}`);
        const sortedImportedFilePaths = importedFilePaths.sort((a, b) => (a > b ? 1 : -1));

        // Favorites first, then device files, then imported files
        const allPaths = [...favoritePaths, ...deviceFilePaths, ...sortedImportedFilePaths];

        const fileTree = FileTree.createTreeFromPaths(allPaths);

        // Reapply selected state and set measurement type labels
        if (fileTree) {
            fileTree.subTreeAsList.forEach((node) => {
                node.isChecked = this.reportService.selectedReports.some((r) => this.compareReportableToNode(r, node));
                if (node.isFile) {
                    node.isFavorite = favoriteFileNames.has(node.nameAndExtension.toLowerCase());
                    node.measurementType = this.getMeasurementTypeLabel(node.path);
                }
            });
        }

        return fileTree;
    }

    private getMeasurementTypeLabel(nodePath: string): string {
        // For uploaded files, derive type from the stored reportable
        const node = new NodeModel(nodePath);
        const reportable = this.uploadedSorXReportables.find((r) => this.compareReportableToNode(r, node));
        if (reportable) {
            if (reportable.data?.type) {
                return this.sorXTypeToLabel(reportable.data.type);
            }
            // Fallback to report type (SORXLD/SORXPDD) for stubs
            if (reportable.type === "SORXLD") return "Leak Inspection";
            if (reportable.type === "SORXPDD") return "Partial Discharge";
        }

        // For device files, use the parent folder name
        const parts = nodePath.split("/");
        if (parts.length >= 2) {
            const parentFolder = parts[parts.length - 2];
            return this.folderNameToLabel(parentFolder);
        }

        return "";
    }

    private folderNameToLabel(folderName: string): string {
        const lower = folderName.toLowerCase();
        if (lower.includes("leak")) return "Leak Inspection";
        if (lower.includes("partial discharge") || lower === "partial discharge") return "Partial Discharge";
        if (lower.includes("mechanical")) return "Mechanical Inspection";
        return folderName;
    }

    private sorXTypeToLabel(type: string): string {
        switch (type) {
            case SorXMeasurementTypeEnum.LeakDetection:
                return "Leak Inspection";
            case SorXMeasurementTypeEnum.PartialDischarge:
                return "Partial Discharge";
            case SorXMeasurementTypeEnum.MechanicalInspection:
                return "Mechanical Inspection";
            case SorXMeasurementTypeEnum.Image:
                return "Image";
            case SorXMeasurementTypeEnum.Video:
                return "Video";
            default:
                return type || "";
        }
    }

    public toggleImportSection(): void {
        this.isImportSectionExpanded = !this.isImportSectionExpanded;
    }

    public async activateModule(moduleInstance: IWorkflowModuleInstance): Promise<void> {
        this.moduleInstance = moduleInstance;
        await this.projectManagementService.getCurrentProject();
    }

    public async onFileCheckedChange(event: CustomEvent): Promise<void> {
        const node = event.detail.node as NodeModel;
        const isChecked = event.detail.isChecked as boolean;

        if (this.fileTree) this.fileTree.isEditing = true;

        if (isChecked) {
            await this.handleFileCheck(node);
        } else {
            this.handleFileUncheck(node);
        }

        if (this.fileTree) this.fileTree.isEditing = false;
    }

    public async onFileFocusedChange(event: CustomEvent): Promise<void> {
        const node = event.detail.node as NodeModel;

        const report = await this.tryCreateReportableMeasurementFromFile(node.path);
        if (!report) {
            Toast.warning(
                "Cannot preview this item. Unsupported or unreadable measurement.",
                "Preview unavailable",
                this.toastId
            );
            return;
        }

        // Open preview module for this measurement, deselect all other measurements
        this.openPreviewModule(report);
    }

    private async handleFileCheck(node: NodeModel): Promise<void> {
        this.fileLoadingCounter++;
        let report: ReportableMeasurement<SorXMeasurement> | undefined;
        let index = -1;
        try {
            index = this.fileList.findIndex(
                (r) => `Device Files/${r.filePath}` === node.path || `Favorites/${r.filePath}` === node.path
            );
            report = await this.tryCreateReportableMeasurementFromFile(node.path);
            if (report) {
                this.reportService.addReport(report, index);
            } else {
                node.isChecked = false;
                this.fileTree?.disableFile(node, false);
            }
        } finally {
            this.fileLoadingCounter--;
        }
    }

    private handleFileUncheck(node: NodeModel): void {
        // For imported files, try to find the reportable and use its ID
        const { reportable, isImported } = this.getMetadataAndReportable(node.path);
        if (isImported && reportable) {
            this.reportService.deselectReport(sorXFilePathToId(reportable.id));
            return;
        }

        // Normalize favorites path to device files path for consistent ID generation
        const normalizedPath = node.path.startsWith(this.favoritesPrefix)
            ? this.deviceFilesPrefix + node.path.substring(this.favoritesPrefix.length)
            : node.path;

        // For device files, compute the ID directly from the file path (including prefix, without extension)
        const id = sorXFilePathToId(normalizedPath);
        this.reportService.deselectReport(id);
    }

    private async tryCreateReportableMeasurementFromFile(
        filePath: string
    ): Promise<ReportableMeasurement<SorXMeasurement> | undefined> {
        if (this.reportableFilePromise[filePath]) {
            return this.reportableFilePromise[filePath];
        }

        // Check if this is an imported file first
        const { metadata, reportable, isImported } = this.getMetadataAndReportable(filePath);
        if (isImported && reportable) {
            return this.validateAndReturnReportable(reportable, metadata);
        }

        // Normalize favorites path to device files path for consistent ID generation
        const normalizedPath = filePath.startsWith(this.favoritesPrefix)
            ? this.deviceFilesPrefix + filePath.substring(this.favoritesPrefix.length)
            : filePath;

        // Fetch metadata from device
        let deviceMetadata: SorXMetadataFull | undefined;
        try {
            deviceMetadata = await this.camApi.downloadSorXMeasurementMetadata(this.removePathPrefix(filePath));
        } catch {
            Toast.warning(
                "This file could not be downloaded. It might be corrupted or inaccessible.",
                "Download failed",
                this.toastId
            );
            return;
        }
        return this.createAndValidateReportableFromDevice(deviceMetadata, normalizedPath);
    }

    private getMetadataAndReportable(filePath: string): {
        metadata?: SorXMetadataFull;
        reportable?: ReportableMeasurement<SorXMeasurement>;
        isImported: boolean;
    } {
        const normalize = (p: string) => p.replace(/^.*[\\/]/, "").toLowerCase();
        const imported = this.uploadedSorXReportables.find((r) => {
            return (
                r.id === filePath ||
                filePath.endsWith("/" + r.id) ||
                normalize(r.id) === normalize(filePath) ||
                normalize(r.name || "") === normalize(filePath)
            );
        });
        if (imported) {
            const data = imported.data as unknown;
            if (data && typeof data === "object" && "type" in data) {
                return { metadata: data as SorXMetadataFull, reportable: imported, isImported: true };
            }
            return { metadata: undefined, reportable: imported, isImported: true };
        }
        return { metadata: undefined, reportable: undefined, isImported: false };
    }

    private validateAndReturnReportable(
        reportable: ReportableMeasurement<SorXMeasurement>,
        metadata?: SorXMetadataFull
    ): ReportableMeasurement<SorXMeasurement> | undefined {
        if (!metadata) {
            Toast.warning(
                "No metadata found for this file. It might be corrupted or unsupported.",
                "Unsupported file",
                this.toastId
            );
            return;
        }
        if (!metadata.type || !this.isSupportedMeasurementType(metadata.type)) {
            Toast.warning(
                `${metadata?.type?.toUpperCase() || "Unknown"} files are not supported.`,
                "Unsupported file",
                this.toastId
            );
            return;
        }
        const reportType = reportable.type as string;
        if (!this.isSupportedReportType(reportType)) {
            Toast.warning(
                `${reportType.toUpperCase() || "Unknown"} report type is not supported.`,
                "Unsupported Report Type",
                this.toastId
            );
            return;
        }
        return reportable;
    }

    private createAndValidateReportableFromDevice(
        metadata: SorXMetadataFull | undefined,
        filePath: string
    ): ReportableMeasurement<SorXMeasurement> | undefined {
        if (!metadata) {
            Toast.warning(
                "No metadata found for this file. It might be corrupted or unsupported.",
                "Unsupported file",
                this.toastId
            );
            return;
        }
        if (!metadata.type || !this.isSupportedMeasurementType(metadata.type)) {
            Toast.warning(
                `${metadata?.type?.toUpperCase() || "Unknown"} files are not supported. Only LI and PDI files can be edited.`,
                "Unsupported file",
                this.toastId
            );
            return;
        }
        let reportable: ReportableMeasurement<SorXMeasurement>;
        try {
            reportable = sorXMeasurementToReportableMeasurement(metadata, filePath);
        } catch (error) {
            console.error(error, "Unable to convert measurement");
            Toast.warning(
                "The file was downloaded but could not be converted. Please check the format or contact support.",
                "Conversion Failed",
                this.toastId
            );
            return;
        }
        const reportType = reportable.type as string;
        if (!this.isSupportedReportType(reportType)) {
            Toast.warning(
                `${reportType.toUpperCase() || "Unknown"} report type is not supported.`,
                "Unsupported Report Type",
                this.toastId
            );
            return;
        }
        return reportable;
    }

    private isSupportedMeasurementType(type: string): boolean {
        return [SorXMeasurementTypeEnum.LeakDetection, SorXMeasurementTypeEnum.PartialDischarge].includes(
            type as SorXMeasurementTypeEnum
        );
    }

    private isSupportedReportType(type: string): boolean {
        return ["SORXLD", "SORXPDD"].includes(type);
    }

    public async editFile(file: NodeModel): Promise<void> {
        if (!this.reportService.canEditOrGenerateReport) {
            return;
        }
        this.openEditModule();
    }

    private openEditModule(): void {
        if (!this.moduleInstance) return;

        this.moduleInstance
            .createModal<EditMeasurementModalState, void>(WorkflowModalCatalog.EditMeasurementModal)
            .withState({
                onMeasurementsChanged: () => {
                    this.fileTree?.refresh();
                },
                hasLeakRateConversionFeatureLicense: this.hasLeakRateConversionFeatureLicense,
            })
            .show();
    }

    private openPreviewModule(report: ReportableMeasurement<SorXMeasurement>): void {
        if (!this.moduleInstance) return;

        this.moduleInstance
            .createModal<PreviewMeasurementModalState, void>(WorkflowModalCatalog.PreviewMeasurementModal)
            .withState({ report })
            .show();
    }

    public async generateReport(): Promise<void> {
        if (!this.reportService.canEditOrGenerateReport) {
            return;
        }
        GoogleAnalytics.trackEvent("Industrial_Create_Report", {
            button_name: "Create Report",
            location: "reporting workflow file tree",
        });
        const selectedReports = this.reportService.selectedReports;

        if (selectedReports.length === 0) {
            Toast.warning("No reports selected.", "", this.toastId);
            return;
        } else if (selectedReports.length > 1) {
            const firstType = selectedReports[0].type;
            const allSameType = selectedReports.every((r) => r.type === firstType);

            // Sort selected reports by how they are in the file tree
            selectedReports.sort((a, b) => {
                const aIndex = this.fileTree.subTreeAsList.findIndex((node) => this.compareReportableToNode(a, node));
                const bIndex = this.fileTree.subTreeAsList.findIndex((node) => this.compareReportableToNode(b, node));

                return aIndex - bIndex;
            });

            if (!allSameType) {
                Toast.warning("Cannot generate a combined report from different measurement types.", "", this.toastId);
                return;
            }
        }

        await this.reportGenerator.downloadCombinedPdf(selectedReports);
    }

    public async downloadFile(event: DownloadEvent): Promise<void | Blob> {
        const { reportable, isImported } = this.getMetadataAndReportable(event.file.path);

        if (isImported && reportable) {
            this.downloadImportedSorXFile(event);
            return;
        }
        if (event.file.type === "SorX") {
            GoogleAnalytics.trackEvent("Industrial_Download_File", {
                button_name: "Download File",
                location: "reporting workflow file tree",
            });
            const measurementBlob = await this.camApi.downloadSorXMeasurementFile(
                this.removePathPrefix(event.file.path),
                event.downloadProgressCallback
            );
            FileDownloader.downloadBlobAsFile(
                measurementBlob,
                "application/octet-stream",
                event.file.name + "." + this.sorxExtension
            );
        } else {
            const measurementBlob = await this.camApi.downloadMeasurementFile(
                this.removePathPrefix(event.file.path),
                event.downloadProgressCallback
            );
            if (!event.directly) return measurementBlob;
            FileDownloader.downloadBlobAsFile(measurementBlob, "application/octet-stream", event.file.nameAndExtension);
        }
    }

    public uploadSorXFile = async (file: FileToUpload): Promise<void> => {
        try {
            this.validateSorXFile(file);
            const filePath = this.importedFilesPrefix + file.name;
            let detectedType: string | undefined;
            let measurement: SorXMetadataFull | undefined;

            let parsed: SorXMetadataFull | undefined;
            try {
                parsed = await parseSorXFile(file, (buffer: ArrayBuffer) => this.prpdDataService.parsePrpdBin(buffer));
            } catch {
                // Silent parse failure; will fall back to stub
            }
            if (parsed) {
                detectedType = (parsed as Partial<SorXMetadataFull>).type;
                measurement = parsed;
            }

            let reportable: ReportableMeasurement<SorXMeasurement> | undefined;
            if (measurement && measurement.type && this.isSupportedMeasurementType(measurement.type)) {
                try {
                    reportable = sorXMeasurementToReportableMeasurement(measurement, filePath);
                } catch (err) {
                    // fallback to stub
                }
            }
            if (!reportable) {
                reportable = this.createReportableSorXStub(filePath, detectedType);
            }
            if (!reportable || !this.isSupportedReportType(reportable.type as string)) {
                const msg = `Uploaded file '${file.name}' is not a supported SorX measurement.`;
                file.errorMessage = msg;
                file.status = FileUploadStatus.Error;
                Toast.warning(msg, "Unsupported", this.toastId);
                return;
            }
            this.uploadedSorXReportables.push(reportable);
            this.uploadedSorXSelection[reportable.id] = false;
            file.status = FileUploadStatus.Success;
            // Clear any previously selected reports since the file structure is changing
            this.reportService.removeAllReports();
            // Refresh the main file tree after import
            if (this.fileTree) {
                this.fileTree?.refresh();
            }
            // Close the dropbox after 3 seconds to reset the state
            setTimeout(() => this.fileDropbox?.cancelAction?.(), 3000);
        } catch (e) {
            const errMsg = e instanceof Error ? e.message : "Unknown error";
            file.errorMessage = errMsg;
            file.status = FileUploadStatus.Error;
            Toast.error(`Failed to import '${file.name}': ${errMsg}`, "Import Error", this.toastId);
        }
    };

    private validateSorXFile(file: FileToUpload): void {
        const ext = FileNameExtensions.getExtension(file.name).toLowerCase();
        if (ext !== this.sorxExtension) throw new NotSupportedError("Only .sorx files are accepted.");
        const baseName = FileNameExtensions.getName(file.name).trim();
        if (!baseName) throw new Error("File name cannot be empty.");
        const duplicate = this.uploadedSorXReportables.some(
            (r) => FileNameExtensions.getName((r.name || "").trim()).toLowerCase() === baseName.toLowerCase()
        );
        if (duplicate) throw new Error(`A file with the name '${file.name}' has already been imported.`);
    }

    private createReportableSorXStub(
        baseName: string,
        detectedType?: string
    ): ReportableMeasurement<SorXMeasurement> | undefined {
        let type: string | undefined;
        if (detectedType === "leakDetection") {
            type = "SORXLD";
        } else if (detectedType === "partialDischarge") {
            type = "SORXPDD";
        }
        if (type) {
            return {
                id: sorXFilePathToId(baseName),
                name: baseName + "." + this.sorxExtension,
                company: "Unknown",
                created: new Date(),
                notes: "Uploaded sorx file",
                surveyName: baseName,
                type,
                data: {} as SorXMeasurement,
            };
        }
        return undefined;
    }

    public toggleUploadedSorXSelection(reportable: ReportableMeasurement<SorXMeasurement>): void {
        const current = this.uploadedSorXSelection[reportable.id] || false;
        const next = !current;
        this.uploadedSorXSelection[reportable.id] = next;
        if (next) this.reportService.addReport(reportable);
        else this.reportService.deselectReport(reportable.id);
    }

    // Removes the prefixes like "Device Files/" and "Imported Files/" from the given path
    private removePathPrefix(path: string): string {
        if (path.startsWith(this.favoritesPrefix)) {
            return path.substring(this.favoritesPrefix.length);
        }
        if (path.startsWith(this.deviceFilesPrefix)) {
            return path.substring(this.deviceFilesPrefix.length);
        }
        if (path.includes(this.importedFilesPrefix)) {
            return path.split(this.importedFilesPrefix)[1];
        }
        return path;
    }

    private buildSorXBlobFromReportable(reportable): Blob {
        if (reportable.originalFile) {
            return new Blob([reportable.originalFile], {
                type: "application/octet-stream",
            });
        }

        // fallback
        return new Blob([JSON.stringify(reportable.data)], {
            type: "application/octet-stream",
        });
    }

    private downloadImportedSorXFile = async (event: DownloadEvent): Promise<Blob> => {
        const { reportable } = this.getMetadataAndReportable(event.file.path);

        const blob = this.buildSorXBlobFromReportable(reportable);

        if (!event.directly) {
            return blob;
        }

        FileDownloader.downloadBlobAsFile(
            blob,
            "application/octet-stream",
            reportable.name || `${reportable.id}.${this.sorxExtension}`
        );
        return;
    };

    // At this point we don't know what is what, so we just do this and hope for the best...
    private compareReportableToNode(a: ReportableMeasurement<SorXMeasurement>, b: NodeModel): boolean {
        return a.id === sorXFilePathToId(b.path);
    }
}
